// CallsHub.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../actions';
import { callService } from '../calls';
import {
    fetchMyChats,
    setActiveChatIdLocal,
} from '../actions/chatActions';
import ChatUI from '../containers/ChatPage/ChatUI';
import axios from '../helpers/axios';
import CallFloatingUI from '../components/call/CallFloatingUI';
import SplitPane from './../components/Layout/SplitPane';
import BackButton from '../siteSetting/components/BackButton';

import { socket } from '../containers/ChatPage/socket';
import { receiveAdminMessage, markMessagesAsReadSocket } from '../actions/messageActions';
import { notify } from '../utils/notifications';

import BackDropCallsHub from './BackDropCallsHub';
import ChatListCallsHub from './ChatListCallsHub';
import Loader from '../components/Loader';

export default function CallsHub() {
    const dispatch = useDispatch();

    const me = useSelector((state) => state.auth?.user);
    const users = useSelector((state) => state.user?.users || []);
    const chats = useSelector((state) => state.chat?.chats || []);
    const activeChatId = useSelector((state) => state.chat?.activeChatId);
    const chatError = useSelector((state) => state.chat?.error || null);

    const [composeOpen, setComposeOpen] = useState(false);
    const [view, setView] = useState('chats');
    const [q, setQ] = useState('');

    const [onlineIds, setOnlineIds] = useState(new Set());
    const [lastSeen, setLastSeen] = useState({});
    const [calls, setCalls] = useState([]);

    const [usersLoading, setUsersLoading] = useState(true);
    const [chatsLoading, setChatsLoading] = useState(true);

    const isMobile = useIsMobile(1024);

    useEffect(() => {
        let alive = true;

        setUsersLoading(true);

        Promise.resolve(dispatch(getUsers()))
            .finally(() => {
                if (alive) setUsersLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [dispatch]);

    useEffect(() => {
        let alive = true;

        setChatsLoading(true);

        Promise.resolve(dispatch(fetchMyChats({ context: 'all', type: 'all' })))
            .finally(() => {
                if (alive) setChatsLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [dispatch]);

    useEffect(() => {
        const sock = callService.socket;

        if (!sock) return;

        const onState = ({ online }) => setOnlineIds(new Set((online || []).map(String)));
        const onOnline = ({ userId }) => setOnlineIds((state) => {
            const next = new Set(state);
            next.add(String(userId));
            return next;
        });
        const onOffline = ({ userId }) => setOnlineIds((state) => {
            const next = new Set(state);
            next.delete(String(userId));
            return next;
        });

        sock.on('presence:state', onState);
        sock.on('presence:online', onOnline);
        sock.on('presence:offline', onOffline);

        const hb = setInterval(() => {
            try {
                sock.emit('presence:heartbeat');
            } catch (_error) { }
        }, 30000);

        return () => {
            sock.off('presence:state', onState);
            sock.off('presence:online', onOnline);
            sock.off('presence:offline', onOffline);
            clearInterval(hb);
        };
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get('/admin/presence');
                setLastSeen(data.lastSeen || {});
            } catch (_error) {
                setLastSeen({});
            }
        })();
    }, []);

    useEffect(() => {
        if (view !== 'calls') return;

        (async () => {
            try {
                const { data } = await axios.get('/call');
                setCalls(data.calls || []);
            } catch (_error) {
                setCalls([]);
            }
        })();
    }, [view]);

    useEffect(() => {
        try {
            notify.unlock();
        } catch (_error) { }
    }, []);

    const joinedRoomsRef = useRef(new Set());

    useEffect(() => {
        if (!socket) return;

        const ids = (chats || []).map((chat) => String(chat._id)).filter(Boolean);

        ids.forEach((id) => {
            if (!joinedRoomsRef.current.has(id)) {
                socket.emit('joinChat', id);
                joinedRoomsRef.current.add(id);
            }
        });

        [...joinedRoomsRef.current].forEach((id) => {
            if (!ids.includes(id)) {
                socket.emit('leaveChat', id);
                joinedRoomsRef.current.delete(id);
            }
        });
    }, [chats]);

    useEffect(() => {
        if (!socket) return;

        const rejoin = () => {
            joinedRoomsRef.current.forEach((id) => socket.emit('joinChat', id));
        };

        socket.on('connect', rejoin);

        return () => {
            socket.off('connect', rejoin);
        };
    }, []);

    useEffect(() => {
        if (!socket) return;

        const onNewMessage = (message) => {
            dispatch(receiveAdminMessage(message, me?._id));

            const mine = String(message.sender?._id || message.sender) === String(me?._id);

            if (!mine) {
                try {
                    notify.beep();
                } catch (_error) { }
            }
        };

        const onMessagesRead = ({ chatId, userId }) => {
            dispatch(markMessagesAsReadSocket(chatId, userId));
        };

        socket.on('newMessage', onNewMessage);
        socket.on('messagesRead', onMessagesRead);

        return () => {
            socket.off('newMessage', onNewMessage);
            socket.off('messagesRead', onMessagesRead);
        };
    }, [dispatch, me?._id]);

    const activeChat = useMemo(() => {
        return chats.find((item) => String(item._id) === String(activeChatId));
    }, [chats, activeChatId]);

    const filteredChats = useMemo(() => {
        const value = q.trim().toLowerCase();

        if (!value) return chats;

        return chats.filter((chat) => {
            const title = [
                chat.title,
                chat.user?.firstName,
                chat.user?.lastName,
                chat.user?.email,
                chat.officeId?.name,
                chat.entity?.title,
                ...(Array.isArray(chat.participants)
                    ? chat.participants.map((user) => `${user.firstName || ''} ${user.lastName || ''} ${user.email || ''}`)
                    : []),
            ].filter(Boolean).join(' ').toLowerCase();

            return title.includes(value);
        });
    }, [chats, q]);

    if (isMobile && activeChat) {
        return (
            <div className="callsHub callsHub--chat">
                <div className="callsHub__content" style={{ minHeight: 0, overflow: 'hidden' }}>
                    <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                        <ChatUI
                            chat={activeChat}
                            onBack={() => {
                                dispatch(setActiveChatIdLocal(null));
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="callsHub" style={{ overflow: 'hidden' }}>
            <div className="callsHub__mobileTop">
                <BackButton />

                <div className="callsHub__search">
                    <input
                        value={q}
                        onChange={(event) => setQ(event.target.value)}
                        placeholder="Поиск"
                    />
                </div>

                <button className="iconBtn" onClick={() => setComposeOpen(true)}>✎</button>
            </div>

            <div className="callsHub__content" style={{ minHeight: 0, overflow: 'hidden' }}>
                <SplitPane
                    defaultW={300}
                    headerLeft={(
                        <div className="splitPane__leftHeadTitle">
                            <span className="splitPane__leftHeadText">Чаты</span>
                        </div>
                    )}
                    left={(
                        <div className="callsHub__chats" style={{ minHeight: 0, overflow: 'hidden', display: 'grid', gridTemplateRows: 'auto auto 1fr' }}>
                            <div className="callsHub__search" style={{ margin: 0 }}>
                                <input
                                    value={q}
                                    onChange={(event) => setQ(event.target.value)}
                                    placeholder="Поиск"
                                />
                            </div>

                            {chatError && (
                                <div className="callsHub__error" style={{ padding: '8px 10px', color: '#ef4444', fontSize: 13 }}>
                                    {chatError}
                                </div>
                            )}

                            <div style={{ minHeight: 0, overflow: 'hidden' }}>
                                <ChatListCallsHub
                                    chats={filteredChats}
                                    users={users}
                                    me={me}
                                    activeChatId={activeChatId}
                                    loading={chatsLoading}
                                />
                            </div>
                        </div>
                    )}
                    right={(
                        <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                            {activeChat ? (
                                <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                                    <ChatUI chat={activeChat} />
                                </div>
                            ) : chatsLoading ? (
                                <div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                                    <Loader typeSize="60px" />
                                </div>
                            ) : (
                                <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: '#93a4b8' }}>
                                    Выберите чат слева
                                </div>
                            )}
                        </div>
                    )}
                />
            </div>

            <BackDropCallsHub
                composeOpen={composeOpen}
                setComposeOpen={setComposeOpen}
                isMobile={isMobile}
                me={me}
                setView={setView}
                q={q}
                onlineIds={onlineIds}
                users={users}
                lastSeen={lastSeen}
                usersLoading={usersLoading}
            />

            <CallFloatingUI calls={calls} />
        </div>
    );
}

function useIsMobile(max = 1024) {
    const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    useEffect(() => {
        const on = () => setW(window.innerWidth);

        window.addEventListener('resize', on);

        return () => window.removeEventListener('resize', on);
    }, []);

    return w <= max;
}